import React, { ReactElement, useContext } from 'react';
import { generatePath, matchPath, useHistory, useLocation } from 'react-router';

import { RouteProps, WizardContextType, WizardNavigation } from './Wizard.types';

export const WizardContext = React.createContext<WizardContextType>({
  currentPage: '',
  currentPageIndex: -1,
  fromPath: undefined,
  onValidateSubmit: () => {},
  onWizardDelay: undefined,
  onWizardSubmit: () => {},
  paths: [],
  routesProps: [],
  setShowMandatoryInfoBox: () => {},
  showMandatoryInfoBox: {
    errorInfoBoxContent: null,
    errorInfoBoxExtraMargin: false,
    visible: false,
  },
  state: {},
  updateWizardState: () => {},
});

WizardContext.displayName = 'WizardContext';

export const resolveWizardNextPath = (paths: string[], currentPageIndex: number): string =>
  paths?.[Math.min(Math.max(currentPageIndex, -1) + 1, paths.length - 1)];

export const resolveWizardPreviousPath = (paths: string[], currentPageIndex: number): string =>
  paths?.[Math.max(Math.min(currentPageIndex, paths.length) - 1, 0)];

export const getRoutesPropsWithPaths = (routes: ReactElement[]): RouteProps[] =>
  routes
    ?.map?.((route) => {
      const {
        // We don't want to expose children, component or render via the Wizard
        // as they are expensive to pass and we won't use it anyway.
        // We are mostly interested here in props defined in {RouteProps} type
        children,
        component,
        render,
        ...routeProps
      } = route.props as any;

      return routeProps;
    })
    .filter((route) => !!route.path) || [];

export const getRoutesPaths = (routes: ReactElement[]): string[] =>
  getRoutesPropsWithPaths(routes).map((route) => route.path);

export const parseWizardRoute = (path: string): string =>
  // In order to allow wizard routes to navigate to dynamic routes
  // eg. `/vehicle/:id?` or even `/foo/:id?/bar/:step?`
  // we need to parse the route to allow Wizard to perform `history.push`,
  // without placing dynamic part of the link in the browser URL bar.
  // The `generatePath` function will do this for us, as passing `{}`
  // to it will remove all optional params from the routes, and without any
  // magic bespoke code, will output respectively `/vehicle` and `/foo/bar`
  generatePath(path, {});

export const getParsedRoutePaths = (paths: string[]): string[] => paths?.map?.(parseWizardRoute) || [];

export const getCurrentPageIndex = (currentPage: string, paths: string[]): number =>
  paths?.findIndex?.((path) => matchPath(currentPage, { exact: true, path })) ?? -1;

export const useWizard = (): WizardContextType => {
  const wizardContext = useContext(WizardContext);

  return wizardContext;
};

export const useOnWizardDelay = (callBack: () => void): (() => void) => {
  const { onWizardDelay } = useWizard();

  return onWizardDelay ? onWizardDelay.bind(null, callBack) : callBack;
};

export const useCurrentPage = (): string => {
  const location = useLocation();

  return location.pathname;
};

export const useWizardNextPage = (): string => {
  const { currentPageIndex, paths } = useWizard();

  return resolveWizardNextPath(paths, currentPageIndex);
};

export const useWizardPreviousPage = (): string => {
  const { currentPageIndex, paths } = useWizard();

  return resolveWizardPreviousPath(paths, currentPageIndex);
};

export const useWizardGoToPreviousPage = (): (() => void) => {
  const previousPage = useWizardPreviousPage();
  const history = useHistory();

  const goToPrevPage = useOnWizardDelay(() =>
    history.push({
      pathname: previousPage,
      state: {
        ...((history.location.state as Record<string, unknown>) || {}),
        isPrevStep: true,
      },
    }));

  return goToPrevPage;
};

export const useWizardIsLastPage = (): boolean => {
  const { currentPageIndex, paths } = useWizard();

  return currentPageIndex === paths.length - 1;
};

export const useWizardIsFirstPage = (): boolean => {
  const { currentPageIndex } = useWizard();

  return currentPageIndex === 0;
};
export const useWizardNavigation = (): WizardNavigation => {
  const isFirstPage = useWizardIsFirstPage();
  const isLastPage = useWizardIsLastPage();
  const nextPage = useWizardNextPage();
  const previousPage = useWizardPreviousPage();

  return {
    isFirstPage,
    isLastPage,
    nextPage,
    previousPage,
  };
};
